import * as React from 'react';
import styled from 'styled-components';

import { COLORS, BREAKPOINTS } from '../styles';
import { Search, GearSettings, Notification, Language, GridView } from '../icons';
import Avatar, { AvatarContainer } from './Avatar';
import { ActionOptions } from '../types';

const MenuWrapper = styled.div<{ isInMobileMenu?: boolean }>`
  background-color: ${COLORS.white};
  display: flex;
  height: 48px;

  ${BREAKPOINTS.SMALL} {
    height: ${props => (props.isInMobileMenu ? '48px' : '56px')};
  }
  ${BREAKPOINTS.MEDIUM} {
    height: ${props => (props.isInMobileMenu ? '48px' : '64px')};
  }
`;
interface HeaderItemProps {
  hideOnLowWidth: boolean;
  inMobileMenu?: boolean;
}

const HeaderItem = styled.div<HeaderItemProps>`
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  padding: 0;

  display: flex;
  align-items: center;
  text-align: center;
  position: relative;

  color: ${COLORS.black};
  text-decoration: none;

  button {
    margin: 0;
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    bottom: -1px;
    left: 20px;
    right: 20px;
    height: 3px;
    background-color: transparent;
  }
`;

export const ActionButton = styled.button<HeaderItemProps>`
  outline: none;
  background-color: ${COLORS.white};
  border-width: 0;
  margin: 0 6px 0 0;
  padding: 8px;
  cursor: pointer;

  height: 48px;
  width: 48px;

  display: ${props => (props.hideOnLowWidth ? 'none' : 'block')};

  ${BREAKPOINTS.MEDIUM} {
    display: block;
  }

  div {
    height: 32px;
    width: 32px;
    border-radius: 4px;

    svg {
      height: 24px;
      width: 24px;
      padding: 4px;
      color: ${COLORS.neutral_600};
    }
  }

  &:disabled {
    color: ${COLORS.neutral_300};
    background-color: ${COLORS.neutral_100};
  }

  &:hover:not(:disabled) {
    div {
      background-color: ${COLORS.primary_20};
    }
    div svg {
      color: ${COLORS.primary_700};
    }
  }

  &:focus:not(:disabled) {
    div {
      box-shadow: 0px 0px 8px ${COLORS.primary_500}, 0px 4px 12px rgba(46, 127, 161, 0.25);
      outline: none;
    }
    div svg {
      color: ${COLORS.neutral_600};
    }
  }

  &:active:not(:disabled) {
    div {
      background: ${COLORS.primary_100};
      box-shadow: none;
    }
    div svg {
      color: ${COLORS.primary_800};
    }
  }
`;

type Props = {
  firstName?: string;
  lastName?: string;
  actions?: ActionOptions;
  hideOnLowWidth?: boolean;
  isInMobileMenu?: boolean;
  setShowUserMenu: (open: boolean) => void;
  useUserMenu: boolean | undefined;
};

const Actions = ({ actions, firstName, lastName, hideOnLowWidth, isInMobileMenu, setShowUserMenu, useUserMenu }: Props): React.ReactElement<Props> => {
  const searchRef = React.useRef(null);
  const notificationRef = React.useRef(null);
  const languageRef = React.useRef(null);
  const settingsRef = React.useRef(null);
  const userRef = React.useRef(null);
  const appsRef = React.useRef(null);

  const fireAction = (action: (() => void) | undefined, ref: React.MutableRefObject<HTMLButtonElement | null>) => {
    ref?.current?.blur();
    action !== undefined && action();
  };

  const isPressingEnter = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    return false;
  };

  return (
    <MenuWrapper isInMobileMenu={isInMobileMenu}>
      <HeaderItem hideOnLowWidth={hideOnLowWidth || false} inMobileMenu={isInMobileMenu}>
        {actions?.search && (
          <ActionButton
            hideOnLowWidth={hideOnLowWidth || false}
            inMobileMenu={isInMobileMenu}
            ref={searchRef}
            onClick={() => fireAction(actions?.search, searchRef)}
            onKeyDown={(e: any) => (isPressingEnter(e) ? fireAction(actions?.search, searchRef) : null)}>
            <div>
              <Search size="24px" />
            </div>
          </ActionButton>
        )}
        {actions?.notification && !isInMobileMenu && (
          <ActionButton
            hideOnLowWidth={hideOnLowWidth || false}
            inMobileMenu={isInMobileMenu}
            ref={notificationRef}
            onClick={() => fireAction(actions?.notification, notificationRef)}
            onKeyDown={(e: any) => (isPressingEnter(e) ? fireAction(actions?.notification, notificationRef) : null)}>
            <div>
              <Notification size="24px" />
            </div>
          </ActionButton>
        )}
        {actions?.language && (
          <ActionButton
            hideOnLowWidth={hideOnLowWidth || false}
            inMobileMenu={isInMobileMenu}
            ref={languageRef}
            onClick={() => fireAction(actions?.language, languageRef)}
            onKeyDown={(e: any) => (isPressingEnter(e) ? fireAction(actions?.language, languageRef) : null)}>
            <div>
              <Language size="24px" />
            </div>
          </ActionButton>
        )}
        {actions?.settings && (
          <ActionButton
            hideOnLowWidth={hideOnLowWidth || false}
            inMobileMenu={isInMobileMenu}
            ref={settingsRef}
            onClick={() => fireAction(actions?.settings, settingsRef)}
            onKeyDown={(e: any) => (isPressingEnter(e) ? fireAction(actions?.settings, settingsRef) : null)}>
            <div>
              <GearSettings size="24px" />
            </div>
          </ActionButton>
        )}
        {firstName && lastName && !isInMobileMenu && (
          <AvatarContainer
            id="avatarContainer"
            data-testid="testAvatarContainer"
            ref={userRef}
            onClick={useUserMenu ? () => setShowUserMenu(true) : () => fireAction(actions?.user, userRef)}
            onKeyDown={(e: any) => (isPressingEnter(e) ? (useUserMenu ? setShowUserMenu(true) : fireAction(actions?.user, userRef)) : null)}
            inMobileMenu={isInMobileMenu}
            hideOnLowWidth={hideOnLowWidth}>
            <Avatar size={36} firstName={firstName} lastName={lastName} color={COLORS.primary_500} profileMenuLink="#TODO" />
          </AvatarContainer>
        )}
        {actions?.apps && !isInMobileMenu && window.innerWidth > 731 && (
          <ActionButton
            hideOnLowWidth={hideOnLowWidth || false}
            inMobileMenu={isInMobileMenu}
            id="globalNavAppsButton"
            ref={appsRef}
            onClick={() => fireAction(actions?.apps, appsRef)}
            onKeyDown={(e: any) => (isPressingEnter(e) ? fireAction(actions?.apps, appsRef) : null)}>
            <div>
              <GridView size="24px" />
            </div>
          </ActionButton>
        )}
      </HeaderItem>
    </MenuWrapper>
  );
};

export default Actions;
